<script setup lang="ts">
import { Button } from '@/Components/ui/button'
import { ref, computed, watch } from 'vue'
import { openSupport } from '@/composables/useSupport'
import { Head, router } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import LessonActions from '@/Components/Lesson/LessonActions.vue'
import LessonPaymentDialog from '@/Components/Lesson/LessonPaymentDialog.vue'
import ProposalInbox from '@/Components/Lesson/ProposalInbox.vue'
import { sendPayingIfNeeded } from '@/lib/lessonPayment'
import { CreditCard, Calendar, User, AlertTriangle, ExternalLink, Check, Video, Users, FileText, ChevronRight, Clock } from 'lucide-vue-next'

interface Subscription {
  id: number
  teacher_name: string
  teacher_slug: string
  teacher_avatar: string | null
  tier_name: string
  tier_slug: string
  amount: number
  status: string
  created_at: string
  ended_on: string | null
  session_day: string | null
  session_time: string | null
  paused_until: string | null
  pause_starts_after: string | null
  can_pause: boolean
  /** The earliest and latest days lessons may start again, when a pause is still allowed. */
  pause_from: string | null
  pause_until_latest: string | null
}

interface UpcomingLesson {
  id: number
  subscription_id: number
  teacher_name: string | null
  when: string
  status_label: string
  is_paid: boolean
  is_one_off: boolean
  can_change: boolean
  scheduled_at: string
  status: string
}

interface RecentLesson {
  id: number
  subscription_id: number
  teacher_name: string | null
  when: string
  can_complain: boolean
  complaint: { decided: boolean; outcome: string | null } | null
}

interface Proposal {
  id: number
  type: string
  type_label: string
  status: string
  message: string | null
  expires_at: string
  is_mine: boolean
  summary: string
  from_name: string
  subscription_id: number
}

const props = defineProps<{
  subscriptions: Subscription[]
  lessons: UpcomingLesson[]
  proposals: Proposal[]
  credit_balance: number
  recent_lessons: RecentLesson[]
  complaint_reasons: { value: string; label: string }[]
  pause_maximum_weeks: number
  pause_maximum_weeks_a_year: number
  stripe_key: string
}>()

/*
 * Pausing is the student's own decision, so it does not go through a request.
 * They choose the day lessons start again; the weekly hour is kept for them.
 */
const pausingSubscription = ref<Subscription | null>(null)
const resumesOn = ref('')

function openPause(subscription: Subscription) {
  pausingSubscription.value = subscription
  resumesOn.value = subscription.pause_from ?? ''
  pauseError.value = ''
}
const isPausing = ref(false)
const pauseError = ref('')

async function confirmPause() {
  if (!pausingSubscription.value) return

  isPausing.value = true
  pauseError.value = ''

  try {
    const { data } = await window.axios.post(
      `/lesson-changes/arrangements/${pausingSubscription.value.id}/pause`,
      { resumes_on: resumesOn.value }
    )
    successMessage.value = data.message
    pausingSubscription.value = null
    reload()
  } catch (failure: any) {
    pauseError.value =
      failure?.response?.data?.message ??
      Object.values(failure?.response?.data?.errors ?? {}).flat()[0] ??
      'We could not pause your lessons. Please try again.'
  } finally {
    isPausing.value = false
  }
}

function longDate(isoDate: string): string {
  return new Date(`${isoDate}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
}

function reload() {
  router.reload({ only: ['subscriptions', 'lessons', 'recent_lessons', 'proposals', 'credit_balance'] })
}

// A cancelled lesson leaves the list on reload, so its confirmation is shown here.
function onLessonChanged(message?: string) {
  if (message) {
    successMessage.value = message
    setTimeout(() => { successMessage.value = '' }, 5000)
  }

  reload()
}

/*
 * Reporting a problem with a lesson that has happened. Kept on the same page
 * as the lessons, so telling Bindle is easier than telling the bank.
 */
const reporting = ref<number | null>(null)
const reportReason = ref('')
const reportMessage = ref('')
const reportBusy = ref(false)
const reportError = ref('')

function openReport(lesson: RecentLesson) {
  reporting.value = lesson.id
  reportReason.value = props.complaint_reasons[0]?.value ?? ''
  reportMessage.value = ''
  reportError.value = ''
}

async function sendReport() {
  if (!reporting.value) return

  reportBusy.value = true
  reportError.value = ''

  try {
    const { data } = await window.axios.post(`/lesson-changes/lessons/${reporting.value}/complaint`, {
      reason: reportReason.value,
      message: reportMessage.value,
    })
    successMessage.value = data.message
    reporting.value = null
    reload()
  } catch (failure: any) {
    reportError.value =
      failure?.response?.data?.message ??
      Object.values(failure?.response?.data?.errors ?? {}).flat()[0] ??
      'We could not send that. Please try again.'
  } finally {
    reportBusy.value = false
  }
}

/*
 * Asking for an extra lesson or a different weekly time. Neither takes effect
 * on its own — the tutor has to agree — and both are per arrangement, so the
 * open panel is tracked by subscription rather than by a single flag.
 */
const askFor = ref<{ id: number; panel: 'extra' | 'slot' } | null>(null)
const extraAt = ref('')
const slotDay = ref('monday')
const slotTime = ref('17:00')
const askMessage = ref('')
const asking = ref(false)
const askError = ref('')

const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

/**
 * The current slot arrives for reading — "Monday", "5:00 PM" — and a time input
 * wants "17:00". Prefilling with the unconverted string leaves the field blank,
 * which reads as if we did not know when their lesson is.
 */
function asTimeInput(display: string | null): string {
  const parts = display?.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!parts) return '17:00'

  let hour = Number(parts[1]) % 12
  if (parts[3].toUpperCase() === 'PM') hour += 12

  return `${String(hour).padStart(2, '0')}:${parts[2]}`
}

function openAsk(subscription: Subscription, panel: 'extra' | 'slot') {
  askFor.value = { id: subscription.id, panel }
  askError.value = ''
  askMessage.value = ''
  extraAt.value = ''
  slotDay.value = subscription.session_day?.toLowerCase() ?? 'monday'
  slotTime.value = asTimeInput(subscription.session_time)
}

async function sendAsk(payload: Record<string, unknown>) {
  if (!askFor.value) return

  asking.value = true
  askError.value = ''

  try {
    const subscriptionId = askFor.value.id

    await sendPayingIfNeeded(subscriptionId, paymentIntentId =>
      window.axios.post(`/lesson-changes/arrangements/${subscriptionId}/propose`, {
        ...payload,
        message: askMessage.value || null,
        payment_intent_id: paymentIntentId,
      })
    )
    askFor.value = null
    reload()
  } catch (failure: any) {
    askError.value =
      failure?.response?.data?.message ??
      Object.values(failure?.response?.data?.errors ?? {}).flat()[0] ??
      'That request did not go through. Please try again.'
  } finally {
    asking.value = false
  }
}

const lessonsFor = (subscriptionId: number) =>
  props.lessons.filter(lesson => lesson.subscription_id === subscriptionId)

const subscriptionList = ref<Subscription[]>([...props.subscriptions])
watch(() => props.subscriptions, (fresh) => { subscriptionList.value = [...fresh] })
const showCancelModal = ref(false)
const cancelingSubscription = ref<Subscription | null>(null)
const cancelStep = ref<1 | 2>(1)
const selectedReason = ref('')
const additionalComments = ref('')
const isCanceling = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const cancellationReasons = [
  { id: 'too_expensive', label: 'Too expensive for my budget' },
  { id: 'not_using', label: "I'm not using it enough" },
  { id: 'found_alternative', label: 'Found a different tutor/service' },
  { id: 'quality', label: 'Not satisfied with the sessions' },
  { id: 'scheduling', label: 'Session times don\'t work for me' },
  { id: 'temporary', label: 'Taking a break (planning to return)' },
  { id: 'completed', label: 'Achieved my learning goals' },
  { id: 'other', label: 'Other reason' },
]

const activeSubscriptions = computed(() =>
  subscriptionList.value.filter(s => ['active', 'paused', 'pending'].includes(s.status))
)

const pastSubscriptions = computed(() =>
  subscriptionList.value.filter(s => !['active', 'paused', 'pending'].includes(s.status))
)

const combinedReason = computed(() => {
  const reason = cancellationReasons.find(r => r.id === selectedReason.value)
  let result = reason?.label || selectedReason.value
  if (additionalComments.value.trim()) {
    result += ` | Comment: ${additionalComments.value.trim()}`
  }
  return result
})

const canSubmitCancellation = computed(() => {
  return selectedReason.value !== ''
})

// A pending request was authorized, never charged, and grants nothing yet, so
// withdrawing it releases the hold. Ending an active arrangement takes effect at
// once, with a lesson already paid for settled on the cancellation terms.
const isWithdrawingRequest = computed(() => cancelingSubscription.value?.status === 'pending')

function getTierColor(tier: string): string {
  switch (tier) {
    case 'gold':
      return 'bg-amber-100 text-amber-800 border-amber-200'
    case 'silver':
      return 'bg-gray-100 text-gray-800 border-gray-200'
    default:
      return 'bg-blue-100 text-blue-800 border-blue-200'
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800'
    case 'paused':
      return 'bg-blue-100 text-blue-800'
    case 'cancelled':
      return 'bg-red-100 text-red-800'
    case 'rejected':
      return 'bg-red-100 text-red-800'
    case 'pending':
      return 'bg-blue-100 text-blue-800'
    case 'expired':
      return 'bg-gray-100 text-gray-600'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

function getTierIcon(tier: string) {
  switch (tier) {
    case 'gold':
      return Video
    case 'silver':
      return Users
    default:
      return FileText
  }
}

/**
 * The plans, named as the site now names them.
 *
 * These read "group sessions" and "content library access", which are the two
 * things Bindle no longer sells: Bindle Live takes over the silver tier, and the
 * library is part of both plans rather than a plan of its own. A student looking
 * at their own subscription should see the same two names they were sold.
 */
function getTierDescription(tier: string): string {
  switch (tier) {
    case 'gold':
      return 'Weekly one-to-one lessons, plus your tutor’s content library'
    case 'silver':
      return 'Bindle Live, plus your tutor’s content library'
    default:
      return 'Your tutor’s content library'
  }
}

function openCancelModal(subscription: Subscription) {
  cancelingSubscription.value = subscription
  cancelStep.value = 1
  selectedReason.value = ''
  additionalComments.value = ''
  errorMessage.value = ''
  showCancelModal.value = true
}

function closeCancelModal() {
  showCancelModal.value = false
  cancelingSubscription.value = null
  cancelStep.value = 1
  selectedReason.value = ''
  additionalComments.value = ''
  errorMessage.value = ''
}

function goToStep2() {
  cancelStep.value = 2
}

function goBackToStep1() {
  cancelStep.value = 1
}

async function confirmCancel() {
  if (!cancelingSubscription.value) return

  isCanceling.value = true
  errorMessage.value = ''

  const subscriptionId = cancelingSubscription.value.id
  const wasPending = isWithdrawingRequest.value

  try {
    const { data } = await window.axios.post('/student/subscriptions/cancel', {
      subscription_id: subscriptionId,
      reason: combinedReason.value || null,
    })

    const index = subscriptionList.value.findIndex(s => s.id === subscriptionId)
    if (index !== -1) {
      subscriptionList.value[index].status = data.status
    }

    closeCancelModal()

    // Withdrawing a pending request is settled server-side by the time we get
    // here — there is no Stripe cancellation to wait on.
    if (wasPending) {
      successMessage.value = 'Request withdrawn. You have not been charged.'
      setTimeout(() => { successMessage.value = '' }, 5000)
      return
    }

    reload()
    successMessage.value = 'Your lessons have ended. No more lessons will be booked; we have emailed you the details.'
    setTimeout(() => { successMessage.value = '' }, 5000)
  } catch (error: any) {
    errorMessage.value = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to cancel subscription'
  } finally {
    isCanceling.value = false
  }
}

</script>

<template>
  <Head title="Subscriptions" />

  <DashboardLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <Check class="w-5 h-5 text-white" />
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Anything waiting on an answer comes first: a request nobody sees is a
           request nobody answers, and one that lapses leaves the lesson exactly
           as it was, which is rarely what either party wanted. -->
      <!--
        Credit is spent on its own against the next lesson, so there is nothing
        to click. Saying it is there stops "where did my £5 go?" being the way a
        student finds out it worked.
      -->
      <div
        v-if="props.credit_balance > 0"
        class="mb-6 flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 p-4"
      >
        <Check class="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
        <div>
          <p class="font-semibold text-gray-900">You have £{{ props.credit_balance.toFixed(2) }} in credit</p>
          <p class="text-sm text-gray-700">
            It comes off the next lesson we charge you for, automatically. It does not expire, but it is lost if your lessons end.
          </p>
        </div>
      </div>

      <ProposalInbox :proposals="props.proposals" @changed="reload" />

      <!-- Active Subscriptions -->
      <section class="bg-white rounded-2xl shadow-sm p-8">
        <div class="flex items-center gap-3 mb-6">
          <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <CreditCard class="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Active Subscriptions</h2>
            <p class="text-gray-500 text-sm">Your current tutor subscriptions</p>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="activeSubscriptions.length === 0" class="text-center py-12">
          <CreditCard class="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">No active subscriptions</h3>
          <p class="text-gray-500 mb-4">Subscribe to a tutor to get started with personalised learning</p>
          <a
            href="/student/explore"
            class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Browse Tutors
            <ExternalLink class="w-4 h-4 ml-2" />
          </a>
        </div>

        <!-- Subscription Cards -->
        <div v-else class="space-y-4">
          <div
            v-for="subscription in activeSubscriptions"
            :key="subscription.id"
            class="border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors"
          >
            <div class="flex items-start justify-between">
              <div class="flex items-start gap-4">
                <!-- Tutor Avatar -->
                <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                  <img
                    v-if="subscription.teacher_avatar"
                    :src="subscription.teacher_avatar"
                    :alt="subscription.teacher_name"
                    class="w-full h-full object-cover"
                  />
                  <User v-else class="w-7 h-7 text-gray-400" />
                </div>

                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <h3 class="font-semibold text-gray-900">{{ subscription.teacher_name }}</h3>
                    <span
                      :class="[
                        'text-xs px-2 py-0.5 rounded-full font-medium border',
                        getTierColor(subscription.tier_slug)
                      ]"
                    >
                      {{ subscription.tier_name }}
                    </span>
                    <span
                      :class="[
                        'text-xs px-2 py-0.5 rounded-full font-medium',
                        getStatusColor(subscription.status)
                      ]"
                    >
                      {{ subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1) }}
                    </span>
                  </div>

                  <div v-if="subscription.status !== 'pending'" class="flex items-center gap-4 text-sm text-gray-500 mt-2">
                    <span class="flex items-center gap-1">
                      <CreditCard class="w-4 h-4" />
                      £{{ subscription.amount.toFixed(2) }}/lesson
                    </span>
                    <span class="flex items-center gap-1">
                      <Calendar class="w-4 h-4" />
                      Each lesson is charged two days before it
                    </span>
                  </div>
                  <div v-else class="flex items-center gap-4 text-sm text-gray-500 mt-2">
                    <span class="flex items-center gap-1">
                      <CreditCard class="w-4 h-4" />
                      £{{ subscription.amount.toFixed(2) }}/lesson
                    </span>
                  </div>

                  <div v-if="subscription.session_day" class="flex items-center gap-1 text-sm text-blue-600 mt-2">
                    <Calendar class="w-4 h-4" />
                    <span class="font-medium">{{ subscription.session_day }}{{ subscription.session_time ? ` at ${subscription.session_time}` : '' }}</span>
                  </div>

                  <p class="text-xs text-gray-400 mt-2">
                    Started {{ subscription.created_at }}
                  </p>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <a
                  :href="`/student/tutors/${subscription.teacher_slug}`"
                  class="px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  View Tutor
                </a>
                <div class="flex items-center gap-2">
                  <span
                    v-if="subscription.status === 'pending'"
                    class="px-3 py-1.5 text-sm font-medium text-blue-600 flex items-center gap-1.5 bg-blue-50 rounded-lg mr-2"
                  >
                    <Clock class="w-4 h-4" />
                    Awaiting Approval
                  </span>
                  <span
                    v-if="subscription.status === 'active' && !subscription.can_pause && !subscription.paused_until"
                    class="px-3 py-1.5 text-xs text-gray-500"
                  >
                    No pause left this year
                  </span>
                  <button
                    v-if="subscription.can_pause"
                    @click="openPause(subscription)"
                    class="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    Pause
                  </button>
                  <button
                    @click="openCancelModal(subscription)"
                    class="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    {{ subscription.status === 'pending' ? 'Cancel request' : 'End lessons' }}
                  </button>
                </div>
              </div>
            </div>

              <p v-if="subscription.pause_starts_after" class="mt-3 text-sm text-blue-700">
                Your lesson ending {{ subscription.pause_starts_after }} goes ahead as booked. After it, lessons are paused until {{ subscription.paused_until }}, and start again on their own. Your usual time is kept for you.
              </p>
              <p v-else-if="subscription.paused_until" class="mt-3 text-sm text-blue-700">
                Paused until {{ subscription.paused_until }}. Lessons start again on their own, at your usual time, which is kept for you.
              </p>

              <!-- The lessons themselves, where moving and cancelling live. -->
              <div v-if="lessonsFor(subscription.id).length" class="mt-4 border-t border-gray-100 pt-4 space-y-4">
                <div v-for="lesson in lessonsFor(subscription.id)" :key="lesson.id" class="space-y-2">
                  <div class="flex flex-wrap items-center gap-2 text-sm">
                    <span class="font-medium text-gray-900">{{ lesson.when }}</span>
                    <span v-if="lesson.is_one_off" class="rounded-full bg-purple-50 px-2 py-0.5 text-xs text-purple-700">
                      Extra lesson
                    </span>
                    <span
                      class="rounded-full px-2 py-0.5 text-xs"
                      :class="lesson.is_paid ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'"
                    >
                      {{ lesson.is_paid ? 'Paid for' : 'Not charged yet' }}
                    </span>
                  </div>
                  <LessonActions :lesson="lesson" @changed="onLessonChanged" />
                </div>
              </div>

              <div v-if="subscription.status === 'active' && !subscription.pause_starts_after" class="mt-4 border-t border-gray-100 pt-4">
                <p v-if="askError && askFor?.id === subscription.id" class="mb-2 text-sm text-red-700">
                  {{ askError }}
                </p>

                <div v-if="askFor?.id !== subscription.id" class="flex flex-wrap gap-2">
                  <button
                    @click="openAsk(subscription, 'extra')"
                    class="px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Ask for an extra lesson
                  </button>
                  <button
                    @click="openAsk(subscription, 'slot')"
                    class="px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Ask to change the weekly time
                  </button>
                </div>

                <div v-else class="space-y-3 rounded-lg border border-gray-200 p-4">
                  <template v-if="askFor.panel === 'extra'">
                    <p class="text-sm font-medium text-gray-900">Ask for an extra lesson</p>
                    <p class="text-xs text-gray-500">
                      {{ subscription.teacher_name }} has to agree. You are charged the usual
                      £{{ subscription.amount.toFixed(2) }} only once they do; for a lesson less than 48 hours
                      away you confirm the payment now, and it is released if they decline.
                    </p>
                    <label class="block text-xs text-gray-700">
                      Date and time, UK time
                      <input v-model="extraAt" type="datetime-local" class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
                    </label>
                  </template>

                  <template v-else>
                    <p class="text-sm font-medium text-gray-900">Ask to change the weekly time</p>
                    <p class="text-xs text-gray-500">
                      Lessons you have already paid for stay where they are; the rest move. The time is UK time.
                    </p>
                    <div class="flex gap-2">
                      <select v-model="slotDay" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" aria-label="Day">
                        <option v-for="day in weekdays" :key="day" :value="day">
                          {{ day.charAt(0).toUpperCase() + day.slice(1) }}
                        </option>
                      </select>
                      <input v-model="slotTime" type="time" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" aria-label="Time, UK time" />
                    </div>
                  </template>

                  <textarea
                    v-model="askMessage"
                    rows="2"
                    placeholder="Add a note (optional)"
                    class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                  ></textarea>

                  <div class="flex gap-2">
                    <Button
                      size="sm"
                      :disabled="asking || (askFor.panel === 'extra' && !extraAt)"
                      @click="askFor.panel === 'extra'
                        ? sendAsk({ type: 'extra_lesson', scheduled_at: extraAt })
                        : sendAsk({ type: 'slot_change', day: slotDay, time: slotTime })"
                    >
                      {{ asking ? 'Sending…' : 'Send request' }}
                    </Button>
                    <Button variant="ghost" size="sm" :disabled="asking" @click="askFor = null">
                      Back
                    </Button>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </section>

      <!-- Lessons of the last week, with a way to tell Bindle something went wrong. -->
      <section v-if="props.recent_lessons.length" class="rounded-2xl bg-white p-6 shadow-sm">
        <h2 class="text-lg font-semibold text-gray-900">Your recent lessons</h2>
        <p class="mt-1 text-sm text-gray-500">
          Something went wrong with a lesson? Tell us within a week: we will look into it, check what the lesson room recorded, and let you know what we decide.
        </p>

        <div class="mt-4 divide-y divide-gray-100">
          <div v-for="lesson in props.recent_lessons" :key="lesson.id" class="py-3">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <span class="text-sm text-gray-900">{{ lesson.when }} with {{ lesson.teacher_name }}</span>
              <span v-if="lesson.complaint" class="text-xs text-gray-500">
                {{ lesson.complaint.decided ? `Reported — ${lesson.complaint.outcome}` : 'Reported — we are looking into it' }}
              </span>
              <button
                v-else-if="lesson.can_complain && reporting !== lesson.id"
                class="text-sm font-medium text-red-600 hover:text-red-700"
                @click="openReport(lesson)"
              >
                Report a problem
              </button>
            </div>

            <div v-if="reporting === lesson.id" class="mt-3 space-y-3 rounded-lg border border-gray-200 p-4">
              <label class="block text-xs text-gray-700">
                What went wrong
                <select v-model="reportReason" class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
                  <option v-for="reason in props.complaint_reasons" :key="reason.value" :value="reason.value">
                    {{ reason.label }}
                  </option>
                </select>
              </label>
              <textarea
                v-model="reportMessage"
                rows="3"
                placeholder="Tell us what happened"
                class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              ></textarea>
              <p v-if="reportError" class="text-sm text-red-700">{{ reportError }}</p>
              <div class="flex gap-2">
                <Button size="sm" :disabled="reportBusy || reportMessage.trim().length < 10" @click="sendReport">
                  {{ reportBusy ? 'Sending…' : 'Send' }}
                </Button>
                <Button variant="ghost" size="sm" :disabled="reportBusy" @click="reporting = null">
                  Back
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LessonPaymentDialog :stripe-key="props.stripe_key" />

      <!-- Pause -->
      <div
        v-if="pausingSubscription"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        @click.self="pausingSubscription = null"
      >
        <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
          <h3 class="text-lg font-semibold text-gray-900">Pause your lessons</h3>
          <p class="mt-1 text-sm text-gray-500">
            With {{ pausingSubscription.teacher_name }}. Lessons stop and start again on their own — you do
            not need to do anything.
          </p>

          <label class="mt-4 block text-sm font-medium text-gray-700" for="pause-resumes-on">Start lessons again on</label>
          <input
            id="pause-resumes-on"
            v-model="resumesOn"
            type="date"
            :min="pausingSubscription.pause_from ?? undefined"
            :max="pausingSubscription.pause_until_latest ?? undefined"
            class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          />

          <p class="mt-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
            Your usual time is kept for you while you are away. A pause can last up to
            {{ props.pause_maximum_weeks }} weeks, and pauses can add up to {{ props.pause_maximum_weeks_a_year }}
            weeks in any twelve months<template v-if="pausingSubscription.pause_until_latest">, so the latest you can
            choose now is {{ longDate(pausingSubscription.pause_until_latest) }}</template>. A lesson already paid for, or less than
            48 hours away, still goes ahead.
          </p>

          <p v-if="pauseError" class="mt-3 text-sm text-red-700">{{ pauseError }}</p>

          <div class="mt-5 flex gap-2">
            <Button :disabled="isPausing || !resumesOn" @click="confirmPause">
              {{ isPausing ? 'Pausing…' : 'Pause lessons' }}
            </Button>
            <Button variant="ghost" :disabled="isPausing" @click="pausingSubscription = null">
              Back
            </Button>
          </div>
        </div>
      </div>

      <!-- Past Subscriptions -->
      <section v-if="pastSubscriptions.length > 0" class="bg-white rounded-2xl shadow-sm p-8">
        <div class="flex items-center gap-3 mb-6">
          <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <Calendar class="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Past Subscriptions</h2>
            <p class="text-gray-500 text-sm">Your subscription history</p>
          </div>
        </div>

        <div class="space-y-3">
          <div
            v-for="subscription in pastSubscriptions"
            :key="subscription.id"
            class="flex items-center justify-between p-4 border border-gray-100 rounded-xl bg-gray-50"
          >
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                <img
                  v-if="subscription.teacher_avatar"
                  :src="subscription.teacher_avatar"
                  :alt="subscription.teacher_name"
                  class="w-full h-full object-cover"
                />
                <User v-else class="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <span class="font-medium text-gray-700">{{ subscription.teacher_name }}</span>
                  <span
                    :class="[
                      'text-xs px-2 py-0.5 rounded-full font-medium',
                      getStatusColor(subscription.status)
                    ]"
                  >
                    {{ subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1) }}
                  </span>
                </div>
                <span class="text-sm text-gray-500">
                  {{ subscription.tier_name }} - £{{ subscription.amount.toFixed(2) }}/lesson
                </span>
              </div>
            </div>
            <span v-if="subscription.ended_on" class="text-sm text-gray-400">
              {{ subscription.status === 'rejected' ? 'Declined on' : 'Ended' }} {{ subscription.ended_on }}
            </span>
          </div>
        </div>
      </section>

      <!-- Help Section -->
      <section class="bg-blue-50 rounded-2xl p-6 border border-blue-100">
        <h3 class="font-medium text-gray-900 mb-2">Need help with billing?</h3>
        <p class="text-sm text-gray-700">
          If you have questions about your subscription or billing,
          <button type="button" class="font-semibold underline" @click="openSupport()">
            send our support team a message
          </button>.
        </p>
      </section>
    </div>

    <!-- Cancel Confirmation Modal -->
    <Teleport to="body">
      <div
        v-if="showCancelModal"
        class="fixed inset-0 z-50 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div class="flex items-center justify-center min-h-screen p-4">
          <!-- Background overlay -->
          <div
            class="fixed inset-0 bg-gray-500/75 transition-opacity"
            @click="closeCancelModal"
          ></div>

          <!-- Modal panel -->
          <div class="relative bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg">
            <!-- Step 1: Confirmation -->
            <template v-if="cancelStep === 1">
              <div class="bg-white px-6 pt-6 pb-4">
                <div class="flex items-start gap-4">
                  <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertTriangle class="w-6 h-6 text-red-600" />
                  </div>
                  <div class="flex-1">
                    <h3 class="text-lg font-semibold text-gray-900">
                      {{ isWithdrawingRequest ? 'Withdraw Request?' : 'End your lessons?' }}
                    </h3>
                    <p class="mt-2 text-sm text-gray-500">
                      {{ isWithdrawingRequest
                        ? 'Are you sure you want to withdraw your pending request to this tutor?'
                        : 'Are you sure you want to end your lessons with this tutor?' }}
                    </p>
                  </div>
                </div>

                <!-- Subscription Details Card -->
                <div class="mt-5 bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div class="flex items-center gap-3 mb-3">
                    <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      <img
                        v-if="cancelingSubscription?.teacher_avatar"
                        :src="cancelingSubscription.teacher_avatar"
                        :alt="cancelingSubscription.teacher_name"
                        class="w-full h-full object-cover"
                      />
                      <User v-else class="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p class="font-semibold text-gray-900">{{ cancelingSubscription?.teacher_name }}</p>
                      <p class="text-sm text-gray-500">{{ cancelingSubscription?.tier_name }} Plan</p>
                    </div>
                  </div>

                  <div class="space-y-2 text-sm">
                    <div class="flex justify-between">
                      <span class="text-gray-500">Price per lesson</span>
                      <span class="font-medium text-gray-900">£{{ cancelingSubscription?.amount.toFixed(2) }}</span>
                    </div>
                  </div>

                  <!-- What the plan includes, which stops with the lessons -->
                  <div v-if="!isWithdrawingRequest" class="mt-4 pt-3 border-t border-gray-200">
                    <p class="text-xs font-medium text-gray-700 mb-2">What you'll lose:</p>
                    <div class="flex items-center gap-2 text-sm text-gray-600">
                      <component :is="getTierIcon(cancelingSubscription?.tier_slug || '')" class="w-4 h-4 text-gray-400" />
                      <span>{{ getTierDescription(cancelingSubscription?.tier_slug || '') }}</span>
                    </div>
                  </div>
                </div>

                <p v-if="isWithdrawingRequest" class="mt-4 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
                  <strong>Note:</strong> This tutor has not accepted your request yet, so you have not been charged.
                  The payment hold on your card will be released.
                </p>
                <p v-else class="mt-4 text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
                  <strong>Note:</strong> Your lessons stop straight away. A lesson less than two days away is
                  cancelled on the usual terms and paid for in full, because your tutor set the hour aside; anything
                  further away is not charged, and anything already paid for it is refunded.
                </p>
              </div>

              <div class="bg-gray-50 px-6 py-4 flex justify-between">
                <button
                  @click="closeCancelModal"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {{ isWithdrawingRequest ? 'Keep Request' : 'Keep my lessons' }}
                </button>
                <button
                  @click="goToStep2"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
                >
                  Continue
                  <ChevronRight class="w-4 h-4" />
                </button>
              </div>
            </template>

            <!-- Step 2: Reason Selection -->
            <template v-else>
              <div class="bg-white px-6 pt-6 pb-4">
                <h3 class="text-lg font-semibold text-gray-900 mb-2">Why are you cancelling?</h3>
                <p class="text-sm text-gray-500 mb-4">
                  Please let us know why you're cancelling so we can improve our service.
                </p>

                <!-- Reason Dropdown -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Reason for cancellation <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="selectedReason"
                    class="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm bg-white"
                  >
                    <option value="" disabled>Select a reason...</option>
                    <option
                      v-for="reason in cancellationReasons"
                      :key="reason.id"
                      :value="reason.id"
                    >
                      {{ reason.label }}
                    </option>
                  </select>
                </div>

                <!-- Additional Comments (Optional) -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Additional comments <span class="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    v-model="additionalComments"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="Any additional feedback you'd like to share..."
                  ></textarea>
                </div>

                <!-- Inside the dialog: the page behind it is covered while it is open. -->
                <p v-if="errorMessage" class="mt-3 text-sm text-red-700">{{ errorMessage }}</p>
              </div>

              <div class="bg-gray-50 px-6 py-4 flex justify-between">
                <button
                  @click="goBackToStep1"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  @click="confirmCancel"
                  :disabled="isCanceling || !canSubmitCancellation"
                  type="button"
                  class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  <svg v-if="isCanceling" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ isCanceling
                    ? (isWithdrawingRequest ? 'Withdrawing...' : 'Cancelling...')
                    : (isWithdrawingRequest ? 'Confirm Withdrawal' : 'Confirm Cancellation') }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </Teleport>
  </DashboardLayout>
</template>
